import React, { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api from "@/api/client";
import { Card } from "@/components/ui/card";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    PieChart,
    Pie,
    Cell,
    Legend,
} from "recharts";
import { fmtINR } from "@/lib/format";

const COLORS = ["#003F8A", "#0284C7", "#059669", "#D97706", "#DC2626", "#8b5cf6", "#0891b2", "#64748b"];

export default function Reports() {
    const [data, setData] = useState(null);
    const [summary, setSummary] = useState([]);

    useEffect(() => {
        api.get("/reports/dashboard").then((r) => setData(r.data));
        api.get("/reports/tasks-summary").then((r) => setSummary(r.data));
    }, []);

    if (!data) return <div className="text-slate-500 text-sm">Loading…</div>;

    const status = Object.entries(data.by_status).map(([k, v]) => ({ name: k.replace("_", " "), value: v }));
    const priority = Object.entries(data.by_priority).map(([k, v]) => ({ name: k, value: v }));

    return (
        <div className="space-y-6" data-testid="reports-page">
            <PageHeader title="Reports &amp; Analytics" subtitle="Firm-wide performance and workload insights" />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat label="Clients" value={data.totals.clients} />
                <Stat label="Tasks" value={data.totals.tasks} />
                <Stat label="Revenue collected" value={fmtINR(data.totals.revenue_collected)} tone="success" />
                <Stat label="Revenue pending" value={fmtINR(data.totals.revenue_pending)} tone="warning" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="p-6">
                    <h3 className="font-display text-lg font-semibold">Tasks by status</h3>
                    <div className="h-64 mt-3">
                        <ResponsiveContainer>
                            <BarChart data={status} margin={{ top: 6, right: 10, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                                <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                                <Bar dataKey="value" fill="#003F8A" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
                <Card className="p-6">
                    <h3 className="font-display text-lg font-semibold">Tasks by priority</h3>
                    <div className="h-64 mt-3">
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie data={priority} innerRadius={45} outerRadius={90} paddingAngle={3} dataKey="value" nameKey="name">
                                    {priority.map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                                </Pie>
                                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
                <Card className="p-6 lg:col-span-2">
                    <h3 className="font-display text-lg font-semibold">Workload by compliance</h3>
                    <div className="h-72 mt-3">
                        <ResponsiveContainer>
                            <BarChart data={summary} margin={{ top: 6, right: 10, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="compliance_type" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                                <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                                <Bar dataKey="count" fill="#0284C7" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
            </div>
        </div>
    );
}

const Stat = ({ label, value, tone }) => (
    <Card className="p-5">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div
            className={`kpi-value text-3xl mt-2 ${
                tone === "success" ? "text-emerald-600" : tone === "warning" ? "text-amber-600" : "text-slate-900"
            }`}
        >
            {value}
        </div>
    </Card>
);
